
import VueTypedMixin from "vue-typed-mixins";
import FavoriteMixin from "@/mixins/favorite";
import { Prop } from "vue/types/options";
import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import portal from "@vacancorp/portal";
import { convertTimeFromDistance } from "@/mixins/common-functions";
// components
import VacancyRequestButton from "@/components/common/vacancy-request-button.vue";
import FavoriteBtn from "@/components/common/favorite-btn.vue";
import EvacautionDrillCardHeader from "@/components/evacuation-drill-card-header.vue";
import ThroneVacancy from "@/components/maps-view/card-view-throne-vacancy.vue";
// tools
import EventBus from "@/event-bus";
import { navOnGoogleMaps, getCoverImageWebpUrl, getCoverImageUrl } from "@/mixins/common-functions";
import { mapGetters, mapMutations, mapState } from "vuex";
import SameBuildingCardHeader from "@/components/same-building-card-header.vue";
import { showServiceUnavailable } from "@/mixins/common-functions/service-unavailable";
import { translateI18n } from "@/util/i18n.helper";

export default VueTypedMixin(FavoriteMixin).extend({
  name: "component-card-view",
  mixins: [FavoriteMixin],
  components: {
    ThroneVacancy,
    VacancyRequestButton,
    FavoriteBtn,
    EvacautionDrillCardHeader,
    SameBuildingCardHeader,
  },
  props: {
    showThroneCard: { type: Boolean, default: false },
    inputData: {
      type: Object as Prop<portal.PublicPlaceWithVacancyId>,
      required: false,
    },
    vacancyStatus: {
      type: Object as Prop<PortalAdapterApi.ResponseGetMergedDisplayVacancy>,
      required: false,
    },
    hideImageList: { type: Boolean, default: false },
    isCardViewDown: { type: Boolean, default: false },
    sameBuildingMode: { type: Boolean, default: false },
    isDesktop: { type: Boolean, default: false },
    inFavoriteList: { type: Boolean, default: false },
  },
  data() {
    return {
      showFavoriteAnime: false,
      playFavoriteOnAnime: false,
      playFavoriteOffAnime: false,
    };
  },
  computed: {
    ...mapState("data", ["selectedPlaceIdHash"]),
    // getters
    ...mapGetters("ui", ["isMap", "isMobile"]),
    ...mapGetters("data", ["getLanguageWithoutCountry", "getLanguageWithCountryOnly"]),
    /** @remarks design of card header on mobile map-view
     * is shown on selected card only, and others are hidden
     * otherwise, the header is shown directly on pc, table and on other views */
    checkIfSetCardHeaderShown(): string {
      if (this.isMobileMapView && this.isSelectedPlace && (this.hasSameBuilding || this.hasEvacuationDrill)) {
        this.setStringCardHeaderShown(this.selectedPlaceIdHash);
      }
      return this.inputData.placeIdHash;
    },
    isMobileMapView(): boolean {
      return this.isMobile && this.isMap && this.$route.path.includes("/map");
    },
    isSelectedPlace(): boolean {
      return this.selectedPlaceIdHash === this.inputData.placeIdHash;
    },
    isDisasterPreventionMap(): boolean {
      return this.inputData?.genre.type === "disaster-prevention";
    },
    showServiceUnavailable(): boolean {
      return showServiceUnavailable(this.inputData);
    },
    statusIsNotReady(): boolean {
      if (this.vacancyStatus === undefined) return false;
      return this.vacancyStatus.status === "notReady";
    },
    showLoaderGif(): string {
      return require("@/assets/images/animation/loader.gif");
    },
    showDefaultThumbnail(): string {
      return require("@/assets/images/default-thumbnail.svg");
    },
    buildingIdHash(): string {
      return this.inputData?.building?.buildingIdHash ?? "";
    },
    buildingName(): string {
      return this.inputData?.building?.name ?? "";
    },

    showCardHeaderOnList(): boolean {
      // pc list or mobile list with sameBuilding or evacuationDrill
      return (
        (!this.isMap && this.hasSameBuilding && !this.sameBuildingMode) ||
        (this.hasSameBuilding && this.isDesktop) ||
        (this.hasEvacuationDrill && this.isDesktop) ||
        (!this.isMap && this.hasEvacuationDrill)
      );
      // mobile map is different design
    },
    pcSelectedCard(): boolean {
      return this.isDesktop && this.selectedPlaceIdHash === this.inputData.placeIdHash;
    },
    showRelatedOpeningHours(): boolean {
      return this.inputData.relatedOpeningHours !== undefined && this.inputData.relatedOpeningHours !== "";
    },
    showRelatedBudget(): string {
      const lang = this.$store.getters["data/getLanguageWithoutCountry"];
      switch (lang) {
        case "ja": {
          return this.inputData.relatedBudget === undefined || this.inputData.relatedBudget === ""
            ? ""
            : this.inputData.relatedBudget + "前後";
        }
        default: {
          return this.inputData.relatedBudget === undefined || this.inputData.relatedBudget === ""
            ? ""
            : this.inputData.relatedBudget;
        }
      }
    },

    hasTakeoutInfo(): boolean {
      return this.inputData.takeoutInfo !== undefined;
    },
    hasAvailableCoupon(): boolean {
      return this.inputData.hasAvailableCoupon;
    },
    hasSameBuilding(): boolean {
      return this.inputData.building !== undefined;
    },
    hasEvacuationDrill(): boolean {
      return this.inputData.isInEvacuationDrill;
    },
    showGenre(): string {
      return translateI18n(this.inputData.genre.displayTextI18n);
    },
    getCoverImageListLength(): number {
      return this.inputData.coverImageList === undefined ? 0 : this.inputData.coverImageList.length;
    },
    showWalkerImage(): boolean {
      return this.getTimeFromDistance !== "";
    },
    getTimeFromDistance(): string {
      const result = this.$store.getters["data/getDistanceFromCurrentLocation"](this.inputData.location);
      if (result === "") return "";

      const km = Number(result);
      return convertTimeFromDistance(km * 1000);
    },
  },
  mounted() {
    this.setIsFavoriteOn(this.inputData.isFavorite);
    this.setShowFavoriteBtn(this.inputData.genre.type !== "disaster-prevention");
  },
  methods: {
    ...mapMutations("data", ["setSelectedPlaceIdHash"]),
    ...mapMutations("ui", ["setStringCardHeaderShown"]),
    notReadyClicked() {
      this.setSelectedPlaceIdHash(this.inputData.placeIdHash);
      if (this.getLanguageWithoutCountry === "ja") {
        this.$router
          .push(`/place/${this.inputData.placeIdHash}#pr-section`)
          .catch(() => console.warn("catch push detail"));
      } else {
        const country = this.getLanguageWithCountryOnly === "us" ? "en" : this.getLanguageWithCountryOnly;
        this.$router
          .push(`/${country}/place/${this.inputData.placeIdHash}#pr-section`)
          .catch(() => console.warn("catch push detail"));
      }
    },
    getCoverImageWebpUrl(image: PortalAdapterApi.PublicImage) {
      return getCoverImageWebpUrl(image);
    },
    getCoverImageUrl(image: PortalAdapterApi.PublicImage) {
      return getCoverImageUrl(image);
    },
    mouseEnter() {
      this.$emit("mouse-enter", this.inputData.placeIdHash);
    },
    mouseLeave() {
      this.$emit("mouse-leave", this.inputData.placeIdHash);
    },
    navBtnClicked(placeName: string, placeLocation: PortalAdapterApi.Location) {
      navOnGoogleMaps(placeName, placeLocation);
    },
    createFavoritePlace(placeIdHash: string) {
      this.FAVORITE_CHECKED(placeIdHash, () => {
        this.UPDATE_SORTED_PLACE_LIST({
          placeIdHash,
          status: true,
        });
      });
    },
    deleteFavoritePlace(placeIdHash: string) {
      this.FAVORITE_UNCHECKED(placeIdHash, () => {
        if (this.inFavoriteList) {
          this.$emit("favorite-unchecked-list", placeIdHash);
          this.UPDATE_PLACE_LIST({
            placeIdHash,
            status: false,
          });
        }
        this.UPDATE_SORTED_PLACE_LIST({
          placeIdHash,
          status: false,
        });
      });
    },
    showSameBuildingList() {
      this.setSelectedPlaceIdHash(this.inputData.placeIdHash);
      if (this.hasSameBuilding !== undefined) {
        this.$router.push(`/building/${this.buildingIdHash}`).catch(() => console.warn(`cannot push to building`));
      }
    },
    cardViewClicked(place: portal.PublicPlaceWithVacancyId) {
      EventBus.$emit("search-again", false);
      if (this.isCardViewDown) {
        this.$emit("move-card-view-up");
        return;
      }
      this.setSelectedPlaceIdHash(place.placeIdHash);
      this.$emit("card-view-clicked", place.placeIdHash);
      this.$router.push(`/place/${place.placeIdHash}`).catch(() => console.warn("catch push detail"));
    },
  },
});
